import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Brain, Heart, Droplets, X, Clock, Pill, Calendar } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { headachesAPI, tensionsAPI, llorosAPI } from '../services/api';
import { Headache, Tension, Lloro, HeadacheFormData, TensionFormData, LloroFormData } from '../types';
import { HeadacheForm } from '../components/forms/HeadacheForm';
import { TensionForm } from '../components/forms/TensionForm';
import { LloroForm } from '../components/forms/LloroForm';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  parseISO,
  startOfWeek,
  endOfWeek
} from 'date-fns';
import { es } from 'date-fns/locale';

type EventType = 'headache' | 'tension' | 'lloro';

interface DayEvents {
  headaches: Headache[];
  tensions: Tension[];
  lloros: Lloro[];
}

export const CalendarPage: React.FC = () => {
  const { user } = useAuth();
  const { canUse } = useSubscription();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [headaches, setHeadaches] = useState<Headache[]>([]);
  const [tensions, setTensions] = useState<Tension[]>([]);
  const [lloros, setLloros] = useState<Lloro[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addType, setAddType] = useState<EventType>('headache');

  // Check if user can see premium features
  const canUseTensions = canUse('tensions');
  const canUseLloros = canUse('lloros');

  useEffect(() => {
    loadMonthData();
  }, [currentMonth]);

  const loadMonthData = async () => {
    setIsLoading(true);
    try {
      const startDate = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
      const endDate = format(endOfMonth(currentMonth), 'yyyy-MM-dd');

      const [headachesRes, tensionsRes, llorosRes] = await Promise.all([
        headachesAPI.getAll({ startDate, endDate, limit: 100 }),
        canUseTensions ? tensionsAPI.getAll({ startDate, endDate, limit: 100 }) : Promise.resolve({ data: { tensions: [] } }),
        canUseLloros ? llorosAPI.getAll({ startDate, endDate, limit: 100 }) : Promise.resolve({ data: { lloros: [] } }),
      ]);

      setHeadaches(headachesRes.data.headaches || []);
      setTensions(tensionsRes.data.tensions || []);
      setLloros(llorosRes.data.lloros || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getDaysInMonth = () => {
    const start = startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  };

  const getEventsForDay = (date: Date): DayEvents => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return {
      headaches: headaches.filter(h => h.date.startsWith(dateStr)),
      tensions: tensions.filter(t => t.date.startsWith(dateStr)),
      lloros: lloros.filter(l => l.date.startsWith(dateStr)),
    };
  };

  const hasEvents = (date: Date) => {
    const events = getEventsForDay(date);
    return events.headaches.length > 0 || events.tensions.length > 0 || events.lloros.length > 0;
  };

  const handlePrevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const handleNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

  const handleDayClick = (date: Date) => {
    setSelectedDate(date);
  };

  const openAddModal = (type: EventType) => {
    setAddType(type);
    setShowAddModal(true);
  };

  const selectedDayEvents = selectedDate ? getEventsForDay(selectedDate) : null;
  const weekDays = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

  // El calendario usa los MISMOS formularios que Inicio para que el registro sea
  // identico en ambos sitios. Cada onSubmit crea con la fecha seleccionada y
  // refresca el mes; si la API falla, el propio formulario muestra el error y el
  // modal no se cierra (la excepcion se propaga a su try/catch interno).
  const createHeadache = async (data: HeadacheFormData) => {
    await headachesAPI.create(data);
    await loadMonthData();
    setShowAddModal(false);
  };
  const createTension = async (data: TensionFormData) => {
    await tensionsAPI.create(data);
    await loadMonthData();
    setShowAddModal(false);
  };
  const createLloro = async (data: LloroFormData) => {
    await llorosAPI.create(data);
    await loadMonthData();
    setShowAddModal(false);
  };

  const ModalContent = () => {
    const cancel = () => setShowAddModal(false);
    const day = selectedDate ?? undefined;
    if (addType === 'headache') return <HeadacheForm onSubmit={createHeadache} onCancel={cancel} initialDate={day} />;
    if (addType === 'tension') return <TensionForm onSubmit={createTension} onCancel={cancel} initialDate={day} />;
    return <LloroForm onSubmit={createLloro} onCancel={cancel} initialDate={day} />;
  };


  return (
    <div>
      <Header title="Calendario" />

      <main className="px-4 sm:px-6 max-w-6xl mx-auto">
        {/* Month Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-6"
        >
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={handlePrevMonth}
            className="p-3 rounded-xl hover:bg-[var(--color-surface)] transition-colors"
          >
            <ChevronLeft size={24} className="text-[var(--color-text)]" />
          </motion.button>

          <h2 className="text-xl font-display font-semibold text-[var(--color-text)] capitalize">
            {format(currentMonth, 'MMMM yyyy', { locale: es })}
          </h2>

          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={handleNextMonth}
            className="p-3 rounded-xl hover:bg-[var(--color-surface)] transition-colors"
          >
            <ChevronRight size={24} className="text-[var(--color-text)]" />
          </motion.button>
        </motion.div>

        {/* Desktop: Side by side layout */}
        <div className="lg:grid lg:grid-cols-5 lg:gap-8">
          {/* Calendar Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="card p-4 mb-6 lg:mb-0 lg:col-span-3"
          >
            {/* Weekday Headers */}
            <div className="grid grid-cols-7 mb-2">
              {weekDays.map((day) => (
                <div key={day} className="text-center text-sm font-medium text-[var(--color-text-muted)] py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Days Grid */}
            <div className="grid grid-cols-7 gap-1">
              {getDaysInMonth().map((day, index) => {
                const isCurrentMonth = isSameMonth(day, currentMonth);
                const isSelected = selectedDate && isSameDay(day, selectedDate);
                const isToday = isSameDay(day, new Date());
                const dayEvents = getEventsForDay(day);
                const hasAnyEvents = hasEvents(day);

                return (
                  <motion.button
                    key={day.toISOString()}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.005 }}
                    onClick={() => handleDayClick(day)}
                    className={`
                      relative aspect-square flex flex-col items-center justify-center rounded-xl
                      transition-all duration-200 lg:p-2
                      ${!isCurrentMonth ? 'opacity-30' : ''}
                      ${isSelected ? 'bg-[var(--color-accent)] text-white' : ''}
                      ${isToday && !isSelected ? 'ring-2 ring-[var(--color-accent)]' : ''}
                      ${!isSelected ? 'hover:bg-[var(--color-bg)]' : ''}
                    `}
                  >
                    <span className={`text-sm lg:text-base font-medium ${isSelected ? 'text-white' : 'text-[var(--color-text)]'}`}>
                      {format(day, 'd')}
                    </span>

                    {/* Event Emojis */}
                    {hasAnyEvents && (
                      <div className="flex gap-0.5 mt-0.5 text-[10px] lg:text-xs">
                        {dayEvents.headaches.length > 0 && (
                          <span>🤕</span>
                        )}
                        {dayEvents.tensions.length > 0 && (
                          <span>❤️</span>
                        )}
                        {dayEvents.lloros.length > 0 && (
                          <span>💧</span>
                        )}
                      </div>
                    )}
                  </motion.button>
                );
              })}
            </div>

            {/* Legend */}
            <div className="flex justify-center gap-4 mt-4 pt-4 border-t border-[var(--color-border)]">
              <div className="flex items-center gap-1.5">
                <span className="text-sm">🤕</span>
                <span className="text-xs text-[var(--color-text-muted)]">Dolores</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-sm">❤️</span>
                <span className="text-xs text-[var(--color-text-muted)]">Tensión</span>
              </div>
              {canUseLloros && (
                <div className="flex items-center gap-1.5">
                  <span className="text-sm">💧</span>
                  <span className="text-xs text-[var(--color-text-muted)]">Lloro</span>
                </div>
              )}
            </div>
          </motion.div>

          {/* Selected Day Details - Sidebar on desktop */}
          <div className="lg:col-span-2">
            <AnimatePresence mode="wait">
              {selectedDate && (
                <motion.div
                  key={selectedDate.toISOString()}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="space-y-4"
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-display font-semibold text-[var(--color-text)]">
                      {format(selectedDate, "d 'de' MMMM", { locale: es })}
                    </h3>
                    <div className="flex gap-2">
                      <motion.button
                        whileTap={{ scale: 0.95 }}
                        onClick={() => openAddModal('headache')}
                        className="p-2 rounded-xl transition-colors"
                        style={{ background: '#E8927C20' }}
                      >
                        <Brain size={20} style={{ color: '#E8927C' }} />
                      </motion.button>
                      <motion.button
                        whileTap={{ scale: 0.95 }}
                        onClick={() => openAddModal('tension')}
                        className="p-2 rounded-xl transition-colors"
                        style={{ background: '#9CAF8820' }}
                      >
                        <Heart size={20} style={{ color: '#9CAF88' }} />
                      </motion.button>
                      {canUseLloros && (
                        <motion.button
                          whileTap={{ scale: 0.95 }}
                          onClick={() => openAddModal('lloro')}
                          className="p-2 rounded-xl transition-colors"
                          style={{ background: '#7DD3FC20' }}
                        >
                          <Droplets size={20} style={{ color: '#7DD3FC' }} />
                        </motion.button>
                      )}
                    </div>
                  </div>

                  {/* Events List */}
                  {selectedDayEvents && (
                    <div className="space-y-3">
                      {/* Headaches */}
                      {selectedDayEvents.headaches.map((h) => (
                        <motion.div
                          key={h.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          className="card p-4 flex items-center gap-4"
                        >
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl intensity-${h.intensity}`}>
                            {h.intensity === 'leve' ? '😊' : h.intensity === 'moderada' ? '😐' : '😰'}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <Brain size={16} style={{ color: '#E8927C' }} />
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full intensity-${h.intensity}`}>
                                {h.intensity}
                              </span>
                              {!!h.medication_taken && <Pill size={14} className="text-[var(--color-success)]" />}
                            </div>
                            <p className="text-sm text-[var(--color-text-muted)] mt-1 flex items-center gap-1">
                              <Clock size={12} />
                              {format(parseISO(h.date), 'HH:mm')}
                            </p>
                          </div>
                        </motion.div>
                      ))}

                      {/* Tensions */}
                      {selectedDayEvents.tensions.map((t) => (
                        <motion.div
                          key={t.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          className="card p-4 flex items-center gap-4"
                        >
                          <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: '#9CAF8820' }}>
                            <Heart size={24} style={{ color: '#9CAF88' }} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-lg font-semibold text-[var(--color-text)]">
                                {t.systolic}/{t.diastolic}
                              </span>
                              {t.pulse && (
                                <span className="text-sm text-[var(--color-text-muted)]">
                                  {t.pulse} bpm
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-[var(--color-text-muted)] mt-1 flex items-center gap-1">
                              <Clock size={12} />
                              {t.time?.substring(0, 5) || '00:00'}
                            </p>
                          </div>
                        </motion.div>
                      ))}

                      {/* Lloros */}
                      {selectedDayEvents.lloros.map((l) => (
                        <motion.div
                          key={l.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          className="card p-4 flex items-center gap-4"
                        >
                          <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: '#7DD3FC20' }}>
                            <Droplets size={24} style={{ color: '#7DD3FC' }} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full intensity-${l.intensity}`}>
                                {l.intensity}
                              </span>
                            </div>
                            <p className="text-sm text-[var(--color-text-muted)] mt-1 flex items-center gap-1">
                              <Clock size={12} />
                              {format(parseISO(l.date), 'HH:mm')}
                            </p>
                          </div>
                        </motion.div>
                      ))}

                      {/* Empty State */}
                      {selectedDayEvents.headaches.length === 0 &&
                       selectedDayEvents.tensions.length === 0 &&
                       selectedDayEvents.lloros.length === 0 && (
                        <div className="card p-8 text-center">
                          <p className="text-[var(--color-text-muted)]">
                            Sin registros este día
                          </p>
                          <p className="text-sm text-[var(--color-text-muted)] mt-1">
                            Usa los botones de arriba para añadir
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </motion.div>
              )}

              {/* No date selected state */}
              {!selectedDate && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="card p-8 text-center"
                >
                  <Calendar size={48} className="mx-auto mb-4 text-[var(--color-text-muted)]" />
                  <p className="text-[var(--color-text-muted)]">
                    Selecciona un día para ver los registros
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </main>

      {/* Add Modal */}
      <AnimatePresence>
        {showAddModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="modal-overlay"
              onClick={() => setShowAddModal(false)}
            />

            {/* Mobile: modal centrado y flotante (antes era una hoja inferior) */}
            <div className="lg:hidden fixed inset-0 z-[101] flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 12 }}
              transition={{ type: 'spring', damping: 26, stiffness: 320 }}
              className="w-full max-w-md rounded-3xl shadow-2xl overflow-hidden pointer-events-auto max-h-[88vh]"
              style={{ background: 'var(--color-surface)' }}
            >
              <div className="p-6 overflow-y-auto max-h-[88vh]" style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom, 1.25rem))' }}>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-display font-semibold text-[var(--color-text)]">
                    {addType === 'headache' && 'Registrar dolor de cabeza'}
                    {addType === 'tension' && 'Registrar tensión arterial'}
                    {addType === 'lloro' && 'Registrar episodio de lloro'}
                  </h3>
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="p-2 rounded-xl hover:bg-[var(--color-bg)] transition-colors"
                  >
                    <X size={24} className="text-[var(--color-text-muted)]" />
                  </button>
                </div>
                <ModalContent />
              </div>
            </motion.div>
            </div>

            {/* Desktop: Centered modal */}
            <div className="hidden lg:flex fixed inset-0 z-[101] items-center justify-center p-4 pointer-events-none">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="w-full max-w-md bg-[var(--color-surface)] rounded-3xl shadow-xl p-6 pointer-events-auto max-h-[80vh] overflow-y-auto"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-display font-semibold text-[var(--color-text)]">
                    {addType === 'headache' && 'Registrar dolor de cabeza'}
                    {addType === 'tension' && 'Registrar tensión arterial'}
                    {addType === 'lloro' && 'Registrar episodio de lloro'}
                  </h3>
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="p-2 rounded-xl hover:bg-[var(--color-bg)] transition-colors"
                  >
                    <X size={24} className="text-[var(--color-text-muted)]" />
                  </button>
                </div>
                <ModalContent />
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};
